import {
  CrossCircleIcon,
  ImageIcon,
  RightArrowIcon,
  TrashAltIcon
} from "assets/icons";
import cn from "classnames";
import { DirectUploadMedia } from "libs/directUpload";
import { Category, useGetAllCategories } from "modules/categories";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { IconButton } from "ui";

import {
  AFFINITY_SOURCE_COLORS,
  AFFINITY_SOURCE_LABELS
} from "../../constants";
import { AffinityRule } from "../../recommendation.types";
import { Badge } from "../Badge";

import styles from "./AffinityGroups.module.scss";

interface AffinityGroupsProps {
  rules: AffinityRule[];
  isLoading?: boolean;
  emptyText?: string;
  onDelete: (rule: AffinityRule) => void;
  onBlock: (rule: AffinityRule) => void;
}

interface Group {
  fromId: string;
  fromName: string;
  rules: AffinityRule[];
}

const AffinityGroups = ({
  rules,
  isLoading,
  emptyText,
  onDelete,
  onBlock
}: AffinityGroupsProps) => {
  const { t } = useTranslation();

  const { categories, isLoading: isCategoriesLoading } = useGetAllCategories();

  // The endpoint returns a category TREE (main → child → sub-child); affinity
  // rules reference any level, so flatten it to look images up by id.
  const imageById = useMemo(() => {
    const map = new Map<string, DirectUploadMedia | undefined>();

    const walk = (nodes: Category[]) => {
      nodes.forEach((node) => {
        map.set(node.id, node.image);
        if (node.childCategories?.length) {
          walk(node.childCategories);
        }
      });
    };

    walk(categories);

    return map;
  }, [categories]);

  const renderThumb = (categoryId: string) => {
    if (isCategoriesLoading) {
      return <span className={cn(styles.thumb, styles.thumb_loading)} />;
    }

    const image = imageById.get(categoryId);

    return (
      <span className={styles.thumb}>
        {image?.url ? (
          <img src={image.url} alt="" loading="lazy" />
        ) : (
          <ImageIcon width={14} height={14} />
        )}
      </span>
    );
  };

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();

    rules.forEach((rule) => {
      const existing = map.get(rule.fromCategoryId);
      if (existing) {
        existing.rules.push(rule);
      } else {
        map.set(rule.fromCategoryId, {
          fromId: rule.fromCategoryId,
          fromName: rule.fromName ?? t("Unknown category"),
          rules: [rule]
        });
      }
    });

    return [...map.values()].sort((a, b) =>
      a.fromName.localeCompare(b.fromName)
    );
  }, [rules, t]);

  if (isLoading) {
    return <Skeleton count={4} height={90} borderRadius={14} />;
  }

  if (!rules.length) {
    return (
      <p className={styles.empty}>
        {emptyText ??
          t("No affinity rules yet — add one above or generate with AI.")}
      </p>
    );
  }

  return (
    <div className={styles.groups}>
      {groups.map((group) => (
        <div key={group.fromId} className={styles.group}>
          <div className={styles.group__from}>
            {renderThumb(group.fromId)}
            <span className={styles.group__fromName}>{group.fromName}</span>
            <span className={styles.group__count}>
              {group.rules.length}{" "}
              {group.rules.length === 1 ? t("pairing") : t("pairings")}
            </span>
          </div>

          <div className={styles.group__rules}>
            {group.rules.map((rule) => (
              <div key={rule.id} className={styles.rule}>
                <RightArrowIcon
                  className={styles.rule__arrow}
                  width={16}
                  height={16}
                />

                {renderThumb(rule.toCategoryId)}

                <span className={styles.rule__to}>
                  {rule.toName ?? t("Unknown category")}
                </span>

                <span className={styles.rule__weight}>
                  {t("weight")} {rule.weight}
                </span>

                <Badge
                  label={AFFINITY_SOURCE_LABELS[rule.source]}
                  color={AFFINITY_SOURCE_COLORS[rule.source]}
                />

                <IconButton
                  className={styles.rule__delete}
                  title={t("Block this pairing")}
                  icon={
                    <CrossCircleIcon fill="#8e8e93" width={18} height={18} />
                  }
                  onClick={() => onBlock(rule)}
                />

                <IconButton
                  className={styles.rule__delete}
                  icon={
                    <TrashAltIcon fill="#ff0033" width={18} height={18} />
                  }
                  onClick={() => onDelete(rule)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AffinityGroups;
